// src/lib/auth/auth-context.tsx
"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  useCallback,
} from "react";
import { 
  signIn, 
  signUp, 
  signOut, 
  getCurrentUser, 
  confirmSignUp,
  autoSignIn,
  resetPassword,
  confirmResetPassword,
  resendSignUpCode,
  type SignUpInput,
} from '@aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { UserRole } from '@/lib/db/schema';
import { useRouter } from 'next/navigation';

// Add utility function at the top
function setAuthCookie(userId: string | null) {
  if (userId) {
    document.cookie = `authSession=${userId}; path=/; secure; samesite=strict; max-age=2592000`; // 30 days
  } else {
    document.cookie = 'authSession=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=strict';
  }
}

// Define custom event names
export const AUTH_EVENTS = {
  VERIFICATION_NEEDED: 'VERIFICATION_NEEDED',
  VERIFICATION_SUCCESS: 'VERIFICATION_SUCCESS',
};

// Global state for verification
export const VerificationState = {
  pendingEmail: null as string | null,
  setPendingEmail(email: string | null) {
    this.pendingEmail = email;
    if (email) {
      // Also dispatch Hub event as a backup
      Hub.dispatch('auth', { 
        event: AUTH_EVENTS.VERIFICATION_NEEDED, 
        data: { email } 
      });
      
      // Store in sessionStorage as a fallback
      try {
        sessionStorage.setItem('pendingVerificationEmail', email);
      } catch (e) {
        console.error('Failed to store in sessionStorage:', e);
      }
    } else {
      try {
        sessionStorage.removeItem('pendingVerificationEmail');
      } catch (e) {
        console.error('Failed to remove from sessionStorage:', e);
      }
    }
  },
  getPendingEmail() {
    if (!this.pendingEmail) {
      try {
        const storedEmail = sessionStorage.getItem('pendingVerificationEmail');
        if (storedEmail) {
          this.pendingEmail = storedEmail;
        }
      } catch (e) {
        console.error('Failed to get from sessionStorage:', e);
      }
    }
    return this.pendingEmail;
  }
};

interface User {
  userId: string;
  email: string;
  name: string;
  emailVerified: boolean;
  credits?: number;
}

interface AuthContextType {
  user: User | null;
  credits: number | null;
  isLoading: boolean;
  fetchUserCredits: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ success: boolean, error?: string, errorCode?: string }>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<{ success: boolean, needsVerification: boolean, existing?: boolean, message?: string }>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  resetPassword: (email: string) => Promise<boolean>;
  confirmResetPassword: (username: string, code: string, newPassword: string) => Promise<void>;
  resendVerificationCode: (email: string) => Promise<void>;
  isAwaitingPasswordResetConfirmation: boolean;
  passwordResetEmail: string | null;
  cancelPasswordReset: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAwaitingPasswordResetConfirmation, setIsAwaitingPasswordResetConfirmation] = useState(false);
  const [passwordResetEmail, setPasswordResetEmail] = useState<string | null>(null);
  const router = useRouter();

  const fetchUserCredits = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data && typeof data.data.credits === 'number') {
          setCredits(data.data.credits);
        }
      }
    } catch (error) {
      console.error("Failed to fetch user credits:", error);
    }
  }, [user]);

  // Function to fetch and set user data
  const fetchUserData = async () => {
    if (!mounted) return;
    
    try {
      const session = await getCurrentUser();
      if (!session || !mounted) {
        throw new Error('No session');
      }

      const userData = {
        userId: session.userId,
        email: session.signInDetails?.loginId || '',
        name: session.username || '',
        emailVerified: true,
      };
      setUser(userData);
      setAuthCookie(session.userId);
      await fetchUserCredits();
    } catch (error) {
      console.error('Error fetching user:', error);
      if (mounted) {
        setUser(null);
        setAuthCookie(null);
        setCredits(null);
      }
    } finally {
      if (mounted) {
        setIsLoading(false);
      }
    }
  };

  let mounted = true;

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      if (!mounted) return;
      
      try {
        const session = await getCurrentUser();
        if (!session || !mounted) {
          throw new Error('No session');
        }

        const userData = {
          userId: session.userId,
          email: session.signInDetails?.loginId || '',
          name: session.username || '',
          emailVerified: true,
        };
        setUser(userData);
        setAuthCookie(session.userId);
        
        // Ensure user role is set with all required fields
        if (userData.email) {
          const userName = userData.name || userData.email.split('@')[0];
          try {
            await fetch(`/api/auth/role?userId=${userData.userId}&email=${encodeURIComponent(userData.email)}&name=${encodeURIComponent(userName)}`, {
              method: 'GET'
            });
          } catch (e) {
            console.error('Error ensuring user role during init:', e);
          }
        }
      } catch (error) {
        if (mounted) {
          setUser(null);
          setAuthCookie(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initAuth();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (!mounted) return;

      switch (payload.event) {
        case 'signedIn':
          initAuth();
          break;
        case 'signedOut':
          if (mounted) {
            setUser(null);
            setAuthCookie(null);
            setCredits(null);
            setIsLoading(false);
          }
          break;
        case 'tokenRefresh':
          initAuth();
          break;
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const handleSignIn = useCallback(async (email: string, password: string): Promise<{ success: boolean, error?: string, errorCode?: string }> => {
    setIsLoading(true);
    try {
      // Check if a user is already signed in and force a full logout if so.
      try {
        await getCurrentUser(); // This will throw if no user is signed in
        // If it does not throw, a user is active. We must clear them out completely.
        await signOut({ global: true });
        setUser(null);
        setAuthCookie(null);
      } catch (e) {
        // This is the expected path for a normal sign-in (no user is currently active).
      }
      
      const signInResult = await signIn({ username: email, password }); // Amplify signIn

      // Handle different signIn results from Amplify
      if (signInResult.isSignedIn) {
        const currentUser = await getCurrentUser(); // Fetch details after successful sign-in
        const userId = currentUser.userId;
        const userName = currentUser.username || email.split('@')[0];

        setAuthCookie(userId);
        
        setUser({
          userId: userId,
          email: currentUser.signInDetails?.loginId || email,
          name: userName,
          emailVerified: true, // Assuming signIn implies email is verified or handled by UserNotConfirmedException
        });

        // Ensure user role is set
        try {
          await fetch(`/api/auth/role?userId=${userId}&email=${encodeURIComponent(email)}&name=${encodeURIComponent(userName)}`, {
            method: 'GET'
          });
        } catch (e) {
          console.error('Error ensuring user role during sign in:', e);
          // Decide if this error should prevent login or just be logged
        }
        
        setIsLoading(false);
        return { success: true };
      } else if (signInResult.nextStep) {
        // Handle cases like CONFIRM_SIGN_UP, etc.
        // This is important if UserNotConfirmedException isn't the only way to get to this state
        setIsLoading(false);
        if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
           VerificationState.setPendingEmail(email); // Ensure pending email is set
           // The page should react to this by showing verification dialog
           // Or, we can return a specific code for the page to handle.
           return { success: false, error: "User is not confirmed.", errorCode: "UserNotConfirmedException" };
        }
        // Handle other next steps as needed
        return { success: false, error: `Unhandled sign-in step: ${signInResult.nextStep.signInStep}`, errorCode: "UnhandledStep" };
      }
      
      // Fallback if signInResult is not as expected but didn't throw
      setIsLoading(false);
      return { success: false, error: "Sign in process did not complete as expected.", errorCode: "UnknownSignInResult" };

    } catch (error: any) {
      // Log a more concise error to the browser console for handled exceptions
      console.error(`AuthContext SignIn Error: ${error.name} - ${error.message}`);
      setIsLoading(false);
      return { success: false, error: error.message || "Failed to sign in.", errorCode: error.name || "UnknownError" };
    }
  }, [router]);

  async function handleSignOut() {
    setIsLoading(true);
    try {
      await signOut({ global: true });
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setUser(null);
      setAuthCookie(null);
      setCredits(null);
      setIsLoading(false);
    }
  }

  // Remove all navigation logic from auth methods
  async function handleSignUp(email: string, password: string, name: string) {
    setIsLoading(true);
    try {
      // Force sign out of any existing user to ensure a clean slate
      await signOut({ global: true });
      // Explicitly clear local state and cookie immediately after sign out
      setUser(null);
      setAuthCookie(null);
      
      const signUpData: SignUpInput = {
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name
          },
          autoSignIn: true
        }
      };

      const { userId, isSignUpComplete, nextStep } = await signUp(signUpData);

      // Store user data in DynamoDB with all required fields
      try {
        const response = await fetch('/api/auth/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId,
            email,
            name: name || email.split('@')[0], // Default to username part of email if name not provided
            role: 'basic', // Default role for new users
            credits: 10, // Assign 10 credits on sign-up
          }),
        });

        if (!response.ok) {
          console.error('Failed to register user in database');
        }
      } catch (error) {
        console.error('Error registering user in database:', error);
      }

      // Set pending verification email in global state
      VerificationState.setPendingEmail(email);
      
      // Emit verification needed event
      Hub.dispatch('auth', { 
        event: AUTH_EVENTS.VERIFICATION_NEEDED, 
        data: { email } 
      });

      return { success: true, needsVerification: true, message: "Please check your email for a verification code" };
    } catch (error: any) {
      // Log a more concise error to the browser console
      console.error(`AuthContext SignUp Error: ${error.name} - ${error.message}`);
      // Re-throwing the error allows the calling page to handle specific cases like UsernameExistsException or InvalidPasswordException with specific toasts
      throw error; 
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResetPassword(email: string): Promise<boolean> {
    setIsLoading(true);
    try {
      const { nextStep } = await resetPassword({ username: email });
      if (nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        setPasswordResetEmail(email);
        setIsAwaitingPasswordResetConfirmation(true);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error(`AuthContext ResetPassword Error: ${error.name} - ${error.message}`);
      setPasswordResetEmail(null);
      setIsAwaitingPasswordResetConfirmation(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  async function handleConfirmResetPassword(username: string, code: string, newPassword: string) {
    setIsLoading(true);
    try {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword,
      });
      setPasswordResetEmail(null);
      setIsAwaitingPasswordResetConfirmation(false);
    } catch (error: any) {
      console.error(`AuthContext ConfirmResetPassword Error: ${error.name} - ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResendVerificationCode(email: string): Promise<void> {
    setIsLoading(true);
    try {
      await resendSignUpCode({ 
        username: email,
        options: {
          clientMetadata: {
            source: 'manual_resend',
            timestamp: new Date().toISOString()
          }
        }
      });
      
      // Emit verification needed event
      Hub.dispatch('auth', { 
        event: AUTH_EVENTS.VERIFICATION_NEEDED, 
        data: { email } 
      });
    } catch (error: any) {
      console.error(`AuthContext ResendVerificationCode Error: ${error.name} - ${error.message}`);
      throw error; // Re-throw so the calling component can notify the user if necessary
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerifyEmail(email: string, code: string) {
    setIsLoading(true);
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code
      });
      
      // After successful verification, the auto-signin process will trigger the
      // Hub listener, which will call initAuth() to set the user session.
      // The call to create the user role has already been done in handleSignUp,
      // so the block that called `getCurrentUser` here was redundant and caused an error.
      
      // Emit verification success event
      Hub.dispatch('auth', { 
        event: AUTH_EVENTS.VERIFICATION_SUCCESS, 
        data: { email } 
      });
      
      // Clear pending verification
      VerificationState.setPendingEmail(null);
      
      // No redirect is needed here. The Hub listener for 'signedIn' will
      // update the application state, and the UI should react to the
      // user becoming authenticated. Forcing a redirect to '/signin'
      // defeats the purpose of the auto-signin flow.

    } catch (error: any) {
      console.error(`AuthContext VerifyEmail Error: ${error.name} - ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  const cancelPasswordReset = () => {
    setPasswordResetEmail(null);
    setIsAwaitingPasswordResetConfirmation(false);
  };

  const value = useMemo(
    () => ({
      user,
      credits,
      isLoading,
      fetchUserCredits,
      signIn: handleSignIn,
      signOut: handleSignOut,
      signUp: handleSignUp,
      verifyEmail: handleVerifyEmail,
      resetPassword: handleResetPassword,
      confirmResetPassword: handleConfirmResetPassword,
      resendVerificationCode: handleResendVerificationCode,
      isAwaitingPasswordResetConfirmation,
      passwordResetEmail,
      cancelPasswordReset,
    }),
    [user, credits, isLoading, isAwaitingPasswordResetConfirmation, passwordResetEmail, fetchUserCredits]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

async function setUserRole(userId: string, roleData: UserRole): Promise<boolean> {
  try {
    const response = await fetch('/api/auth/role', {
      method: 'POST',
      body: JSON.stringify(roleData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to set user role');
    }

    return true;
  } catch (error) {
    console.error('Error setting user role:', error);
    return false;
  }
}
